// Sugalarm — wake-word service bridge (cycle 385). ANDROID ONLY.
//
// The single owner of access to the native `WakeWord` plugin, which drives
// the always-listening foreground service. Registered only in the two
// Android builds, and only when the cycle-383 `VOICE_CAPTURE_ENABLED` build
// switch is on — so `isPresent()` is false on iOS, iPad, the web, and on
// any Android build without the voice layer armed.
//
// Reached through `window.Capacitor.Plugins` — never a bare
// `import("@capacitor/…")` (cycle 26/138/197). Mirrors `lib/voiceCapture.ts`.
//
// ─── THE HALF OF THE MICROPHONE HANDOFF THAT LIVES IN THE SPA ─────────
// The service releases the microphone BEFORE announcing a detection, and
// then waits. `resume()` is how we give it back. It must be called on
// EVERY voice-session-end path — success, miss, error, timeout, and the
// alarm cancel. The service self-heals if we fail to (it re-acquires on a
// timeout), but leaning on that is a bug: the window between a missed
// resume and the timeout is a device that is not listening.

import { capacitorPlatform, isCapacitorNative } from "./capacitor";

/** Mirrors `WakeWordService.State`. */
export type WakeState = "stopped" | "listening" | "yielded" | "error";

export interface WakeStatus {
  /** The OS can host the service at all. */
  supported: boolean;
  micGranted: boolean;
  state: WakeState;
  error: string | null;
  threshold: number;
  /** False until a real wake model ships — the SPA must be able to say
   *  "no model installed" honestly rather than implying a feature that
   *  cannot fire. */
  modelInstalled: boolean;
  detector: string;
}

interface PluginListenerHandle {
  remove: () => Promise<void> | void;
}

interface WakeWordProxy {
  getStatus(): Promise<WakeStatus>;
  start(): Promise<{ started: boolean; error?: string }>;
  stop(): Promise<void>;
  resume(): Promise<void>;
  simulateDetection(): Promise<void>;
  yieldMic(): Promise<void>;
  setThreshold(o: { threshold: number }): Promise<{ threshold: number }>;
  addListener(
    event: "wakeDetected" | "wakeState",
    cb: (data: Record<string, unknown>) => void,
  ): Promise<PluginListenerHandle> | PluginListenerHandle;
}

function plugin(): WakeWordProxy | null {
  if (!isCapacitorNative()) return null;
  if (capacitorPlatform() !== "android") return null;
  const cap = (
    window as { Capacitor?: { Plugins?: Record<string, unknown> } }
  ).Capacitor;
  const p = cap?.Plugins?.WakeWord;
  return p != null ? (p as WakeWordProxy) : null;
}

function devWarn(...args: unknown[]): void {
  try {
    if (localStorage.getItem("sugalarm_voice_dev") === "1") console.warn(...args);
  } catch {
    /* storage unavailable — stay silent */
  }
}

/** True iff the native wake-word plugin is on the bridge. */
export function isPresent(): boolean {
  return plugin() != null;
}

export async function getStatus(): Promise<WakeStatus | null> {
  const p = plugin();
  if (p == null) return null;
  try {
    return await p.getStatus();
  } catch (err) {
    devWarn("[wakeWord] getStatus failed", err);
    return null;
  }
}

/** Start always-listening. Never prompts — a missing grant comes back as
 *  `{started:false, error:"permission"}`. */
export async function start(): Promise<boolean> {
  const p = plugin();
  if (p == null) return false;
  try {
    const r = await p.start();
    return r?.started === true;
  } catch (err) {
    devWarn("[wakeWord] start failed", err);
    return false;
  }
}

export async function stop(): Promise<void> {
  const p = plugin();
  if (p == null) return;
  try {
    await p.stop();
  } catch (err) {
    devWarn("[wakeWord] stop failed", err);
  }
}

/**
 * Give the microphone back after a voice session ends.
 *
 * Idempotent and safe to call when no wake session was in flight — the
 * service ignores a resume it did not yield for, and ignores one entirely
 * while stopped. Call it on EVERY session-end path rather than trying to
 * work out which ones came from a wake.
 */
export async function resume(): Promise<void> {
  const p = plugin();
  if (p == null) return;
  try {
    await p.resume();
  } catch (err) {
    devWarn("[wakeWord] resume failed", err);
  }
}

/**
 * Yield the microphone for a session that did NOT come from a wake — the
 * dev long-press and the diagnostic card.
 *
 * ⚠ Review found this gap: the handoff originally covered only wakes, so
 * every other session contended with the service for the microphone. And
 * because the stub detector can never self-fire, that was in fact 100% of
 * real sessions. Call this before `voiceCapture.startListening()` on any
 * path; it is idempotent and a no-op while the service is stopped.
 */
export async function yieldMic(): Promise<void> {
  const p = plugin();
  if (p == null) return;
  try {
    await p.yieldMic();
  } catch (err) {
    devWarn("[wakeWord] yieldMic failed", err);
  }
}

/** Bench affordance: drive the real detection path with no model. */
export async function simulateDetection(): Promise<void> {
  const p = plugin();
  if (p == null) return;
  try {
    await p.simulateDetection();
  } catch (err) {
    devWarn("[wakeWord] simulateDetection failed", err);
  }
}

export async function setThreshold(threshold: number): Promise<number | null> {
  const p = plugin();
  if (p == null) return null;
  try {
    const r = await p.setThreshold({ threshold });
    return typeof r?.threshold === "number" ? r.threshold : null;
  } catch (err) {
    devWarn("[wakeWord] setThreshold failed", err);
    return null;
  }
}

export interface WakeHandlers {
  onDetected?: (score: number) => void;
  onState?: (state: WakeState, error: string | null) => void;
}

const KNOWN_STATES: ReadonlySet<string> = new Set<WakeState>([
  "stopped",
  "listening",
  "yielded",
  "error",
]);

export function subscribe(handlers: WakeHandlers): () => void {
  const p = plugin();
  if (p == null) return () => {};
  const handles: PluginListenerHandle[] = [];
  let cancelled = false;

  const add = (
    event: "wakeDetected" | "wakeState",
    cb: (d: Record<string, unknown>) => void,
  ) => {
    try {
      Promise.resolve(p.addListener(event, cb))
        .then((h) => {
          if (cancelled) {
            try {
              void h?.remove?.();
            } catch {
              /* ignore */
            }
          } else if (h) handles.push(h);
        })
        .catch((err) => devWarn(`[wakeWord] addListener(${event}) failed`, err));
    } catch (err) {
      devWarn(`[wakeWord] subscribe(${event}) failed`, err);
    }
  };

  add("wakeDetected", (d) => {
    const s = typeof d?.score === "number" ? d.score : 1;
    handlers.onDetected?.(s);
  });
  add("wakeState", (d) => {
    const raw = typeof d?.state === "string" ? d.state : "";
    const st = (KNOWN_STATES.has(raw) ? raw : "error") as WakeState;
    const err = typeof d?.error === "string" ? d.error : null;
    handlers.onState?.(st, err);
  });

  return () => {
    cancelled = true;
    for (const h of handles) {
      try {
        void h.remove();
      } catch {
        /* ignore */
      }
    }
    handles.length = 0;
  };
}
